import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "../../style";
import { formatShortDate } from "../../utils/meetingFormat";
import {
  Highlighted,
  useCallSearch,
  useMeetingSearch,
} from "../../utils/search";
import { Dialog, Icon, Kbd, SectionLabel, Spinner, Tabs, ui } from "../ui";

const style = {
  hit: (on) => ({
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    width: "100%",
    padding: "10px 14px",
    borderRadius: "10px",
    border: 0,
    background: on ? colors.raised : "none",
    color: "inherit",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
  }),
  ico: (color) => ({
    flex: "none",
    width: "30px",
    height: "30px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `${color}29`,
  }),
};

// Searches meetings and calls from anywhere. Meetings only appear when /me
// hands out a meetings search key.
export function SearchDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [tab, setTab] = useState("all");
  const [active, setActive] = useState(0);
  const debounced = useDebounce(text, 200);
  const listRef = useRef(null);

  const meetings = useMeetingSearch(debounced, {
    hitsPerPage: tab === "meetings" ? 20 : 5,
    enabled: open && tab !== "calls",
  });
  const calls = useCallSearch(debounced, {
    hitsPerPage: tab === "calls" ? 20 : 5,
    enabled: open && tab !== "meetings",
  });

  useEffect(() => {
    if (open) {
      setText("");
      setActive(0);
    }
  }, [open]);

  const tabs = [
    { value: "all", label: "All" },
    ...(meetings.available ? [{ value: "meetings", label: "Meetings" }] : []),
    ...(calls.available ? [{ value: "calls", label: "Calls" }] : []),
  ];
  const scope =
    meetings.available && calls.available
      ? "meetings and iPECS calls"
      : meetings.available
        ? "meetings"
        : "iPECS calls";
  const showMeetings = meetings.available && tab !== "calls";
  const showCalls = calls.available && tab !== "meetings";

  const sections = [];
  if (showMeetings && debounced.trim()) {
    sections.push({
      title: "Meetings",
      kind: "meeting",
      query: meetings,
      hits: meetings.data?.hits ?? [],
    });
  }
  if (showCalls && debounced.trim()) {
    sections.push({
      title: "Calls",
      kind: "call",
      query: calls,
      hits: calls.data?.hits ?? [],
    });
  }
  const flat = sections.flatMap((s) =>
    s.hits.map((hit) => ({ kind: s.kind, hit })),
  );
  const loading = sections.some((s) => s.query.isFetching && !s.query.data);
  const failed = sections.some((s) => s.query.isError);

  useEffect(() => setActive(0), [debounced, tab]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = (item) => {
    if (!item) return;
    onClose();
    navigate(
      item.kind === "meeting"
        ? `/meetings/${item.hit.sqid}`
        : `/calls/${encodeURIComponent(item.hit.sqid)}`,
    );
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.max(0, Math.min(i + 1, flat.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[active]);
    }
  };

  let index = 0;

  return (
    <Dialog open={open} onClose={onClose} label="Search" width={680} top="10vh">
      <div style={{ overflow: "hidden", borderRadius: "16px" }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "16px 18px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Icon name="search" size={18} color={colors.textMuted} />
          <input
            autoFocus
            aria-label="Search"
            placeholder={`Search ${scope}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              background: "none",
              color: colors.text,
              fontFamily: "inherit",
              fontSize: "17px",
              outline: "none",
            }}
          />
          {loading && <Spinner size={14} />}
          <Kbd>esc</Kbd>
        </label>

        {tabs.length > 2 && (
          <Tabs
            variant="plain"
            size="sm"
            ariaLabel="Search in"
            value={tab}
            onChange={setTab}
            items={tabs}
            style={{ padding: "10px 14px 0" }}
          />
        )}

        <div
          ref={listRef}
          style={{
            padding: "4px 6px 8px",
            maxHeight: "55vh",
            overflowY: "auto",
          }}
        >
          {!debounced.trim() && (
            <p
              style={{
                ...ui.muted,
                margin: 0,
                padding: "16px 14px",
                fontSize: "13px",
              }}
            >
              Type to search your {scope}.
            </p>
          )}
          {failed && (
            <p style={{ ...ui.error, padding: "12px 14px" }}>
              Search isn't working right now. Try again in a moment.
            </p>
          )}
          {debounced.trim() && !loading && !failed && flat.length === 0 && (
            <p
              style={{
                ...ui.muted,
                margin: 0,
                padding: "16px 14px",
                fontSize: "13px",
              }}
            >
              Nothing matches "{debounced.trim()}".
            </p>
          )}
          {sections.map(
            (s) =>
              s.hits.length > 0 && (
                <div key={s.kind}>
                  <SectionLabel style={{ padding: "10px 14px 4px" }}>
                    {s.title}
                  </SectionLabel>
                  {s.hits.map((hit) => {
                    const i = index++;
                    const meeting = s.kind === "meeting";
                    return (
                      <button
                        key={hit.sqid}
                        type="button"
                        data-index={i}
                        style={style.hit(i === active)}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => go({ kind: s.kind, hit })}
                      >
                        <span
                          style={style.ico(
                            meeting ? colors.green : colors.violet,
                          )}
                        >
                          <Icon
                            name={meeting ? "meetings" : "phone"}
                            size={15}
                            color={meeting ? colors.greenText : "#c3c8ee"}
                          />
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "8px",
                            }}
                          >
                            <strong
                              style={{
                                color: colors.text,
                                fontSize: "14px",
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Highlighted value={hit.title || "Call"} />
                            </strong>
                            {hit.recordedAt && (
                              <span
                                style={{
                                  ...ui.muted,
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatShortDate(
                                  hit.recordedAt,
                                  hit.effectiveTimezone,
                                )}
                              </span>
                            )}
                          </span>
                          {hit.snippet && (
                            <span
                              style={{
                                ...ui.muted,
                                display: "block",
                                fontSize: "13px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Highlighted value={hit.snippet} />
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ),
          )}
        </div>

        <div
          style={{
            ...ui.muted,
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            padding: "10px 18px",
            borderTop: `1px solid ${colors.border}`,
            fontSize: "12px",
          }}
        >
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
          </span>
          <span>
            <Kbd>↵</Kbd> open
          </span>
          <span style={{ flex: 1 }} />
          <span>Searches titles, summaries and what was said</span>
        </div>
      </div>
    </Dialog>
  );
}
